import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import { useDispatch, useSelector } from 'react-redux'
import { createAccount, getConnectCountries, setstartCreatingAccount } from 'features/giving/givingSlice'
import SpinnerSmall from 'components/SpinnerSmall'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

const FALLBACK_COUNTRIES = [
    { code: 'US', name: 'United States', defaultCurrency: 'usd' },
    { code: 'MX', name: 'Mexico', defaultCurrency: 'mxn' },
    { code: 'CA', name: 'Canada', defaultCurrency: 'cad' },
]

const ACCOUNT_TYPES = ['company', 'non_profit', 'individual']

export default function CreateConnectAcctForm() {
    const { t } = useTranslation(['giving', 'common'])
    const dispatch = useDispatch()
    const [country, setCountry] = useState(null)
    const [accountType, setAccountType] = useState('company')

    const {
        createAccountLoading,
        connectCountries,
        getConnectCountriesLoading,
    } = useSelector(state => state.giving)

    const countryOptions = useMemo(
        () => connectCountries?.length ? connectCountries : FALLBACK_COUNTRIES,
        [connectCountries]
    )

    useEffect(() => {
        dispatch(getConnectCountries())
    }, [dispatch])

    useEffect(() => {
        if (!country && countryOptions.length) {
            setCountry(countryOptions.find(option => option.code === 'US') || countryOptions[0])
        }
    }, [country, countryOptions])

    const handleSubmit = async () => {
        if (!country?.code) {
            return toast.error(t('giving:admin.selectCountry'))
        }

        if (!accountType) {
            return toast.error(t('giving:validation.accountType'))
        }

        try {
            const response = await dispatch(createAccount({
                country: country.code,
                accountType,
            })).unwrap()

            dispatch(setstartCreatingAccount(false))

            const onboardingUrl = response?.accountLink?.url
            if (onboardingUrl) {
                window.open(onboardingUrl, '_blank', 'noopener,noreferrer')
            }
        } catch (error) {
            toast.error(error || t('giving:admin.startError'))
        }
    }

    return (
        <SetupShell>
            <SetupCard>
                <IconCircle>$</IconCircle>
                <SetupTitle>{t('giving:admin.setupTitle')}</SetupTitle>
                <SetupCopy>
                    {t('giving:admin.setupBody')}
                </SetupCopy>

                <FieldGroup>
                    <FieldLabel>{t('giving:admin.country')}</FieldLabel>
                    <Autocomplete
                        fullWidth
                        options={countryOptions}
                        value={country}
                        loading={getConnectCountriesLoading}
                        onChange={(_, value) => setCountry(value)}
                        getOptionLabel={(option) => option ? `${option.name} (${option.code})` : ''}
                        isOptionEqualToValue={(option, value) => option.code === value?.code}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder={t('giving:admin.searchCountries')}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {getConnectCountriesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </FieldGroup>

                <FieldGroup>
                    <FieldLabel>{t('giving:admin.accountType')}</FieldLabel>
                    <OptionGrid>
                        {ACCOUNT_TYPES.map((value) => (
                            <OptionButton
                                key={value}
                                type="button"
                                active={accountType === value}
                                onClick={() => setAccountType(value)}
                            >
                                {t(`giving:admin.accountTypes.${value === 'non_profit' ? 'nonProfit' : value}`)}
                            </OptionButton>
                        ))}
                    </OptionGrid>
                </FieldGroup>

                <InfoBox>
                    {t('giving:admin.onboardingHelp')}
                </InfoBox>

                <Actions>
                    <SecondaryButton type="button" onClick={() => dispatch(setstartCreatingAccount(false))}>
                        {t('common:cancel')}
                    </SecondaryButton>
                    <PrimaryButton type="button" disabled={createAccountLoading} onClick={handleSubmit}>
                        {createAccountLoading ? <SpinnerSmall /> : t('giving:admin.startSetup')}
                    </PrimaryButton>
                </Actions>
            </SetupCard>
        </SetupShell>
    )
}

const SetupShell = styled.div`
    width: 100%;
    min-height: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 20px;
`

const SetupCard = styled.div`
    width: min(100%, 680px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding: 28px;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 12px 35px rgba(15, 23, 42, 0.1);
`

const IconCircle = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(0, 180, 204, 0.12);
    color: var(--themeColor);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 28px;
`

const SetupTitle = styled.h2`
    margin: 0;
    color: var(--lightThemeFontColor);
    font-size: 24px;
    font-weight: 700;
    text-align: center;
`

const SetupCopy = styled.p`
    margin: 0;
    max-width: 560px;
    color: #6b7280;
    font-size: 14px;
    line-height: 1.6;
    text-align: center;
`

const FieldGroup = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
`

const FieldLabel = styled.div`
    color: var(--lightThemeFontColor);
    font-size: 13px;
    font-weight: 700;
`

const OptionGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`

const OptionButton = styled.button`
    border: 1px solid ${props => props.active ? 'var(--themeColor)' : '#d1d5db'};
    background: ${props => props.active ? 'var(--themeColor)' : '#ffffff'};
    color: ${props => props.active ? '#ffffff' : '#374151'};
    border-radius: 999px;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
`

const InfoBox = styled.div`
    width: 100%;
    border-radius: 12px;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
    padding: 14px 16px;
    text-align: left;
`

const Actions = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    @media (max-width: 640px) {
        flex-direction: column-reverse;
    }
`

const BaseButton = styled.button`
    min-height: 44px;
    border-radius: 12px;
    padding: 0 18px;
    font-weight: 800;
    cursor: pointer;
`

const PrimaryButton = styled(BaseButton)`
    border: none;
    background: var(--themeColor);
    color: #ffffff;
    min-width: 180px;
    opacity: ${props => props.disabled ? 0.65 : 1};
    pointer-events: ${props => props.disabled ? 'none' : 'auto'};
`

const SecondaryButton = styled(BaseButton)`
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #4b5563;
`
